<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { onClickOutside } from '@vueuse/core';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';
import WhatsAppCampaignDetails from './WhatsAppCampaignDetails.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isWhatsAppType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
  metrics: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits([
  'edit',
  'delete',
  'duplicate',
  'viewReport',
  'viewDetails',
]);

const dropdownRef = ref(null);
const isDropdownOpen = ref(false);

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};

const closeDropdown = () => {
  isDropdownOpen.value = false;
};

onClickOutside(dropdownRef, closeDropdown);

const handleEdit = event => {
  event?.stopPropagation();
  emit('edit');
  closeDropdown();
};

const handleDuplicate = event => {
  event?.stopPropagation();
  emit('duplicate');
  closeDropdown();
};

const handleDelete = event => {
  event?.stopPropagation();
  emit('delete');
  closeDropdown();
};

const handleViewReport = event => {
  event?.stopPropagation();
  emit('viewReport');
  closeDropdown();
};

const handleViewDetails = event => {
  event?.stopPropagation();
  emit('viewDetails');
  closeDropdown();
};

const { t } = useI18n();

const STATUS_COMPLETED = 'completed';
const STATUS_RUNNING = 'running';
const STATUS_DRAFT = 'draft';

const { formatMessage } = useMessageFormatter();

const isDraft = computed(() => props.status === STATUS_DRAFT);
const isRunning = computed(() => props.status === STATUS_RUNNING);
const isCompletedOrRunning = computed(
  () => props.status === STATUS_COMPLETED || props.status === STATUS_RUNNING
);

const isActive = computed(() =>
  props.isLiveChatType
    ? props.isEnabled
    : props.status !== STATUS_COMPLETED &&
      props.status !== STATUS_RUNNING &&
      props.status !== STATUS_DRAFT
);

const statusTextColor = computed(() => ({
  'text-n-teal-11': isActive.value,
  'text-n-amber-11': isRunning.value,
  'text-n-slate-11': isDraft.value,
  'text-n-slate-12': !isActive.value && !isRunning.value && !isDraft.value,
}));

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  if (props.status === STATUS_COMPLETED) {
    return t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED');
  }

  if (props.status === STATUS_DRAFT) {
    return t('CAMPAIGN.WHATSAPP.STATUS.DRAFT');
  }
  return t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
});

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const { medium, channel_type: type } = props.inbox;
  return getInboxIconByType(type, medium);
});
</script>

<template>
  <CardLayout layout="row">
    <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <div class="flex justify-between gap-3 w-fit">
        <span
          class="text-base font-medium capitalize text-n-slate-12 line-clamp-1"
        >
          {{ title }}
        </span>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md bg-n-alpha-2"
          :class="statusTextColor"
        >
          {{ campaignStatus }}
        </span>
      </div>
      <div
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6"
      />
      <div class="flex items-center w-full h-6 gap-2 overflow-hidden">
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <WhatsAppCampaignDetails
          v-else-if="isWhatsAppType"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :metrics="metrics"
        />
        <SMSCampaignDetails
          v-else
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
        />
      </div>
    </div>
    <div class="flex items-center justify-end gap-2">
      <template v-if="!isLiveChatType">
        <Button
          v-if="isCompletedOrRunning"
          variant="faded"
          size="sm"
          color="slate"
          icon="i-lucide-eye"
          :title="t('CAMPAIGN.ACTIONS.VIEW_DETAILS')"
          @click.stop="handleViewDetails"
        />
        <Button
          v-else
          variant="faded"
          size="sm"
          color="slate"
          icon="i-lucide-pencil"
          :title="t('CAMPAIGN.ACTIONS.EDIT')"
          @click.stop="handleEdit"
        />
        <Button
          variant="faded"
          size="sm"
          color="slate"
          icon="i-lucide-copy"
          :title="t('CAMPAIGN.ACTIONS.DUPLICATE')"
          @click.stop="handleDuplicate"
        />
      </template>
      <Button
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-bar-chart-2"
        :title="t('CAMPAIGN.ACTIONS.VIEW_REPORT')"
        @click.stop="handleViewReport"
      />
      <div ref="dropdownRef" class="relative">
        <Button
          variant="faded"
          size="sm"
          color="slate"
          icon="i-lucide-more-vertical"
          :title="t('CAMPAIGN.ACTIONS.MORE')"
          @click.stop="toggleDropdown"
        />
        <div
          v-if="isDropdownOpen"
          class="absolute right-0 top-full z-50 mt-1 w-48 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 py-1"
        >
          <button
            v-if="isCompletedOrRunning"
            class="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
            @click.stop="handleViewDetails"
          >
            <Icon icon="i-lucide-eye" size="16" />
            {{ t('CAMPAIGN.ACTIONS.VIEW_DETAILS') }}
          </button>
          <button
            v-else
            class="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
            @click.stop="handleEdit"
          >
            <Icon icon="i-lucide-pencil" size="16" />
            {{ t('CAMPAIGN.ACTIONS.EDIT') }}
          </button>
          <button
            class="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
            @click.stop="handleDuplicate"
          >
            <Icon icon="i-lucide-copy" size="16" />
            {{ t('CAMPAIGN.ACTIONS.DUPLICATE') }}
          </button>
          <button
            class="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
            @click.stop="handleViewReport"
          >
            <Icon icon="i-lucide-bar-chart-2" size="16" />
            {{ t('CAMPAIGN.ACTIONS.VIEW_REPORT') }}
          </button>
          <button
            class="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
            @click.stop="handleDelete"
          >
            <Icon icon="i-lucide-trash" size="16" />
            {{ t('CAMPAIGN.ACTIONS.DELETE') }}
          </button>
        </div>
      </div>
    </div>
  </CardLayout>
</template>
